import { Fragment } from 'react'

// A small, safe Markdown renderer for answer prose: paragraphs, bullet and
// numbered lists, fenced code, `code`, **bold**, *emphasis* and links.
// It builds React elements (never HTML strings), so model output cannot
// inject markup. Links are limited to http(s) and same-site paths.

const INLINE = /(`[^`\n]+`)|(\*\*[^*\n]+\*\*)|(\*[^*\n\s][^*\n]*\*)|(\[[^\]\n]+\]\([^)\s]+\))/g

function safeHref(href) {
  if (/^https?:\/\//i.test(href)) return { href, external: true }
  if (href.startsWith('/') && !href.startsWith('//')) return { href, external: false }
  return null
}

export function renderInline(text, keyPrefix = 'i', { codeClassName = 'inline-code' } = {}) {
  const source = String(text ?? '')
  const out = []
  let last = 0
  let n = 0
  for (const match of source.matchAll(INLINE)) {
    if (match.index > last) out.push(source.slice(last, match.index))
    const token = match[0]
    const key = `${keyPrefix}-${n++}`
    if (match[1]) {
      out.push(
        <code key={key} className={codeClassName}>
          {token.slice(1, -1)}
        </code>,
      )
    } else if (match[2]) {
      out.push(<strong key={key}>{renderInline(token.slice(2, -2), key, { codeClassName })}</strong>)
    } else if (match[3]) {
      out.push(<em key={key}>{token.slice(1, -1)}</em>)
    } else if (match[4]) {
      const [, label, href] = /^\[([^\]]+)\]\(([^)]+)\)$/.exec(token)
      const safe = safeHref(href)
      out.push(
        safe ? (
          <a key={key} href={safe.href} {...(safe.external ? { target: '_blank', rel: 'noreferrer noopener' } : {})}>
            {label}
          </a>
        ) : (
          label
        ),
      )
    }
    last = match.index + token.length
  }
  if (last < source.length) out.push(source.slice(last))
  return out
}

function parseBlocks(text) {
  const lines = String(text ?? '').replace(/\r\n/g, '\n').split('\n')
  const blocks = []
  let i = 0
  while (i < lines.length) {
    const line = lines[i]
    if (/^\s*```/.test(line)) {
      const code = []
      i++
      while (i < lines.length && !/^\s*```/.test(lines[i])) code.push(lines[i++])
      i++
      blocks.push({ type: 'code', text: code.join('\n') })
      continue
    }
    if (/^\s*([-*•]|\d+[.)])\s+/.test(line)) {
      const ordered = /^\s*\d+[.)]\s+/.test(line)
      const items = []
      while (i < lines.length && /^\s*([-*•]|\d+[.)])\s+/.test(lines[i])) {
        items.push(lines[i].replace(/^\s*([-*•]|\d+[.)])\s+/, ''))
        i++
      }
      blocks.push({ type: ordered ? 'ol' : 'ul', items })
      continue
    }
    if (!line.trim()) {
      i++
      continue
    }
    const para = []
    while (
      i < lines.length &&
      lines[i].trim() &&
      !/^\s*```/.test(lines[i]) &&
      !/^\s*([-*•]|\d+[.)])\s+/.test(lines[i])
    ) {
      para.push(lines[i].replace(/^#{1,6}\s+/, ''))
      i++
    }
    blocks.push({ type: 'p', text: para.join(' ') })
  }
  return blocks
}

export function Markdown({ text, className, codeBlockClassName }) {
  const blocks = parseBlocks(text)
  return (
    <>
      {blocks.map((block, index) => {
        const key = `b${index}`
        if (block.type === 'code') {
          return (
            <pre key={key} className={codeBlockClassName}>
              {block.text}
            </pre>
          )
        }
        if (block.type === 'ul' || block.type === 'ol') {
          const List = block.type
          return (
            <List key={key} className={className}>
              {block.items.map((item, j) => (
                <li key={j}>{renderInline(item, `${key}-${j}`)}</li>
              ))}
            </List>
          )
        }
        return (
          <p key={key} className={className}>
            {renderInline(block.text, key)}
          </p>
        )
      })}
    </>
  )
}

/** Inline-only rendering for one-line fields (flow details, rules, gotchas). */
export function Inline({ text, codeClassName }) {
  return <Fragment>{renderInline(text, 'x', { codeClassName })}</Fragment>
}
